import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import './ReturnRequest.css';

const ReturnRequest = () => {
  const { orderId, itemId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [existingReturns, setExistingReturns] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    order_id: orderId || searchParams.get('order_id') || '',
    order_item_id: itemId || searchParams.get('item_id') || '',
    return_reason: '',
    mention_reason: '',
    item_condition: '',
    return_video: null,
  });
  const [videoPreview, setVideoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/user/orders');
      // Include delivered (can return) and refunded (show "can't file again")
      const ordersForReturn = (response.orders || []).filter(
        order => order.order_status === 'delivered' || order.order_status === 'refunded'
      );
      setOrders(ordersForReturn);

      // If orderId is provided, set it as selected
      const orderIdFromUrl = orderId || searchParams.get('order_id');
      if (orderIdFromUrl) {
        const order = ordersForReturn.find(o => o.id === parseInt(orderIdFromUrl));
        if (order) {
          setSelectedOrder(order.id);
          await fetchOrderItems(order.id);
          const itemIdFromUrl = itemId || searchParams.get('item_id');
          if (itemIdFromUrl) {
            const itemIdNum = parseInt(itemIdFromUrl);
            // Check if this item already has a return request
            const returnsResponse = await api.get(`/returns/order/${order.id}`).catch(() => ({ returns: [] }));
            const returnsList = returnsResponse.returns || [];
            const hasReturn = returnsList.some(
              ret => ret.order_item_id === itemIdNum && ret.status !== 'return_cancelled'
            );
            const hasReturnRejected = returnsList.some(
              ret => ret.order_item_id === itemIdNum && ret.status === 'return_cancelled'
            );
            if (hasReturn) {
              toast.warning('A return request has already been submitted for this item');
            } else if (hasReturnRejected) {
              toast.error("Return is rejected. You can't file.");
            } else {
              setSelectedItem(itemIdNum);
            }
          }
        }
      }
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, [orderId, itemId, searchParams]);

  const fetchOrderItems = useCallback(async (orderId) => {
    try {
      const orderData = await api.get(`/user/orders/${orderId}`);
      setOrderItems(orderData.items || []);
      
      // Fetch existing return requests for this order
      try {
        const returnsResponse = await api.get(`/returns/order/${orderId}`);
        setExistingReturns(returnsResponse.returns || []);
      } catch (error) {
        // If endpoint doesn't exist or fails, set empty array
        setExistingReturns([]);
      }
    } catch (error) {
      toast.error('Failed to fetch order items');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [isAuthenticated, fetchOrders, navigate]);

  useEffect(() => {
    if (selectedOrder) {
      fetchOrderItems(selectedOrder);
      setFormData(prev => ({ ...prev, order_id: selectedOrder, order_item_id: '' }));
      setSelectedItem(null);
    }
  }, [selectedOrder, fetchOrderItems]);

  useEffect(() => {
    if (selectedItem) {
      setFormData(prev => ({ ...prev, order_item_id: selectedItem }));
    }
  }, [selectedItem]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (videoPreview) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  const validateFile = (file) => {
    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    const maxSize = 50 * 1024 * 1024; // 50MB

    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Only video files (MP4, MOV, AVI, WEBM) are allowed.');
      return false;
    }

    if (file.size > maxSize) {
      toast.error('File size must be less than 50MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter(validateFile);

    if (validFiles.length === 0) return;

    // Only allow one video file
    const selectedFile = validFiles[0];
    
    // Cleanup previous preview if exists
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }

    setFormData({ ...formData, return_video: selectedFile });
    
    // Create video preview URL for display
    const previewUrl = URL.createObjectURL(selectedFile);
    setVideoPreview(previewUrl);
  };

  const handleFileInput = (e) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const removeVideo = () => {
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setFormData({ ...formData, return_video: null });
    setVideoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.order_id) {
      toast.error('Please select an order');
      return;
    }

    if (!formData.order_item_id) {
      toast.error('Please select a product to return');
      return;
    }

    if (!formData.return_reason) {
      toast.error('Please select a reason for return');
      return;
    }

    if (!formData.item_condition) {
      toast.error('Please select the condition of the item');
      return;
    }

    if (!formData.return_video) {
      toast.error('Please upload a video file');
      return;
    }

    // Get product_id from selected order item
    const selectedOrderItem = orderItems.find(item => item.id === selectedItem);
    if (!selectedOrderItem || !selectedOrderItem.product_id) {
      toast.error('Unable to find product information');
      return;
    }

    const canReturnItem = selectedOrderItem.returnable === 1 || selectedOrderItem.returnable === true ||
      selectedOrderItem.replacement_allowed === 1 || selectedOrderItem.replacement_allowed === true;
    if (!canReturnItem) {
      toast.error("This item cannot be returned.");
      return;
    }

    if (selectedOrder && orders.find(o => o.id === selectedOrder)?.order_status === 'refunded') {
      toast.error("Refunded. You can't file return again.");
      return;
    }
    const hasReturnRefundedItem = existingReturns.some(
      ret => ret.order_item_id === selectedItem && ret.status === 'refunded'
    );
    if (hasReturnRefundedItem) {
      toast.error("Refunded. You can't file return again.");
      return;
    }
    // Check if this item already has a return request
    const hasExistingReturn = existingReturns.some(
      ret => ret.order_item_id === selectedItem && ret.status !== 'return_cancelled' && ret.status !== 'refunded'
    );
    if (hasExistingReturn) {
      toast.error('A return request has already been submitted for this item');
      return;
    }
    // Check if return was rejected (cancelled) – don't allow filing again
    const hasReturnRejected = existingReturns.some(
      ret => ret.order_item_id === selectedItem && ret.status === 'return_cancelled'
    );
    if (hasReturnRejected) {
      toast.error("Return is rejected. You can't file.");
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('order_id', formData.order_id);
      formDataToSend.append('order_item_id', formData.order_item_id);
      formDataToSend.append('product_id', selectedOrderItem.product_id);
      formDataToSend.append('return_reason', formData.return_reason);
      if (formData.mention_reason) {
        formDataToSend.append('mention_reason', formData.mention_reason);
      }
      if (formData.item_condition) {
        formDataToSend.append('item_condition', formData.item_condition);
      }
      formDataToSend.append('return_video', formData.return_video);

      await api.post('/returns', formDataToSend);

      toast.success('Return request submitted successfully');
      navigate('/orders');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit return request');
    } finally {
      setSubmitting(false);
    }
  };

  const returnReasons = [
    'Damaged Item',
    'Defective Item',
    'Incorrect Item'
  ];

  const selectedOrderData = selectedOrder ? orders.find(o => o.id === selectedOrder) : null;
  const isOrderRefunded = selectedOrderData?.order_status === 'refunded';

  // Selected item eligibility: can return and no existing/rejected/refunded return
  const selectedOrderItem = selectedItem ? orderItems.find(item => item.id === selectedItem) : null;
  const hasSelectedItemRejected = selectedItem && existingReturns.some(
    ret => ret.order_item_id === selectedItem && ret.status === 'return_cancelled'
  );
  const hasSelectedItemRefunded = selectedItem && existingReturns.some(
    ret => ret.order_item_id === selectedItem && ret.status === 'refunded'
  );
  const hasSelectedItemAlreadySubmitted = selectedItem && existingReturns.some(
    ret => ret.order_item_id === selectedItem && ret.status !== 'return_cancelled' && ret.status !== 'refunded'
  );
  const selectedItemCanReturn = selectedOrderItem && (
    selectedOrderItem.returnable === 1 || selectedOrderItem.returnable === true ||
    selectedOrderItem.replacement_allowed === 1 || selectedOrderItem.replacement_allowed === true
  );
  const isSelectedItemEligible = selectedItem && !hasSelectedItemRejected && !hasSelectedItemRefunded && !hasSelectedItemAlreadySubmitted && selectedItemCanReturn && !isOrderRefunded;

  const hasExistingReturns = existingReturns.some(ret => ret.status !== 'return_cancelled' && ret.status !== 'refunded');
  const hasRejectedReturns = existingReturns.some(ret => ret.status === 'return_cancelled');
  const hasRefundedReturns = existingReturns.some(ret => ret.status === 'refunded');
  const hasNonReturnableItems = orderItems.some(
    item => !(item.returnable === 1 || item.returnable === true || item.replacement_allowed === 1 || item.replacement_allowed === true)
  );

  const canSubmit = !!(
    selectedOrder &&
    selectedItem &&
    isSelectedItemEligible &&
    !isOrderRefunded &&
    formData.return_reason &&
    formData.item_condition &&
    formData.return_video &&
    !submitting
  );

  if (loading) return null;

  return (
    <Container className="return-request-page">
      <h1 className="return-request-title">Initiate A Return</h1>

      {isOrderRefunded && (
        <Alert variant="warning" className="return-refunded-banner mb-4">
          <strong>Refunded.</strong> You can&apos;t file return again for this order.
        </Alert>
      )}

      <div className="return-request-layout">
        {/* Left Side - Return Request Form */}
        <div className={`return-request-form ${isOrderRefunded ? 'return-form-disabled' : ''}`}>
          <h2 className="form-title">Return Request Details</h2>
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <Form.Label>Select Order *</Form.Label>
              <Form.Select
                value={formData.order_id}
                onChange={(e) => {
                  const orderId = e.target.value;
                  setSelectedOrder(orderId ? parseInt(orderId) : null);
                }}
                required
                disabled={isOrderRefunded}
              >
                <option value="">Choose an order</option>
                {orders.map((order) => (
                  <option key={order.id} value={order.id}>
                    Order #{order.order_number} - {new Date(order.created_at).toLocaleDateString()}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Select Product to Return *</Form.Label>
              <Form.Select
                value={formData.order_item_id}
                onChange={(e) => setSelectedItem(e.target.value ? parseInt(e.target.value) : null)}
                required
                disabled={!selectedOrder || isOrderRefunded}
              >
                <option value="">Choose a product</option>
                {orderItems.map((item) => {
                  const hasReturnRefunded = existingReturns.some(
                    ret => ret.order_item_id === item.id && ret.status === 'refunded'
                  );
                  const hasReturnRejected = existingReturns.some(
                    ret => ret.order_item_id === item.id && ret.status === 'return_cancelled'
                  );
                  const hasReturnRequest = existingReturns.some(
                    ret => ret.order_item_id === item.id && ret.status !== 'return_cancelled' && ret.status !== 'refunded'
                  );
                  const canReturn = item.returnable === 1 || item.returnable === true || item.replacement_allowed === 1 || item.replacement_allowed === true;
                  const disabled = hasReturnRefunded || hasReturnRejected || hasReturnRequest || !canReturn;
                  let label = item.item_type === 'combo' || item.combo_id
                    ? `${item.product_name} (Combo${item.combo_selections_detail?.length ? `: ${item.combo_selections_detail.map((d) => [d.product_name, d.size].filter(Boolean).join(' ')).join(' + ')}` : ''})`
                    : `${item.product_name} ${item.color_name && item.size ? `(${item.color_name}, ${item.size})` : ''}`;
                  if (hasReturnRefunded) label += " - Refunded. You can't file again";
                  else if (hasReturnRejected) label += " - Return rejected. You can't file again";
                  else if (hasReturnRequest) label += ' - Return Request Already Submitted';
                  else if (!canReturn) label += " - Item can't be returned";
                  return (
                    <option
                      key={item.id}
                      value={item.id}
                      disabled={disabled}
                    >
                      {label}
                    </option>
                  );
                })}
              </Form.Select>
              {selectedOrderItem &&
                (selectedOrderItem.item_type === 'combo' || selectedOrderItem.combo_id) && (
                <div className="mt-3 p-3 border rounded" style={{ background: '#fafafa' }}>
                  <div className="small fw-semibold mb-2">
                    Combo includes
                    <span className="ms-2 badge" style={{ background: '#16a34a' }}>COMBO</span>
                  </div>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {(selectedOrderItem.combo_selections_detail || []).map((d, idx) => (
                      <div
                        key={d.variant_id || idx}
                        style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                      >
                        {d.product_image && (
                          <img
                            src={d.product_image}
                            alt=""
                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4 }}
                          />
                        )}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{d.product_name}</div>
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {[d.color_name, d.size].filter(Boolean).join(' • ')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="return-info-cards mt-3">
                {hasExistingReturns && (
                  <Alert variant="info" className="return-info-card mb-2 mb-md-2">
                    <strong>Return already submitted</strong>
                    <span className="d-block mt-1 small">Items with existing return requests are disabled. You can only return each item once.</span>
                  </Alert>
                )}
                {hasRefundedReturns && (
                  <Alert variant="warning" className="return-info-card mb-2 mb-md-2">
                    <strong>Refunded</strong>
                    <span className="d-block mt-1 small">Refunded. You can&apos;t file return again for that item.</span>
                  </Alert>
                )}
                {hasRejectedReturns && (
                  <Alert variant="danger" className="return-info-card mb-2 mb-md-2">
                    <strong>Return rejected</strong>
                    <span className="d-block mt-1 small">Return was rejected. You can&apos;t file again for that item.</span>
                  </Alert>
                )}
                {hasNonReturnableItems && (
                  <Alert variant="warning" className="return-info-card mb-2 mb-md-2">
                    <strong>Item not returnable</strong>
                    <span className="d-block mt-1 small">Items that cannot be returned are disabled. Only products with return or replacement allowed can be returned.</span>
                  </Alert>
                )}
                {selectedItem && !isSelectedItemEligible && (
                  <Alert variant="danger" className="return-info-card mb-2 mb-md-2">
                    <strong>Cannot submit</strong>
                    <span className="d-block mt-1 small">
                      {isOrderRefunded
                        ? "Refunded. You can't file return again."
                        : hasSelectedItemRefunded
                          ? "Refunded. You can't file return again for this item."
                          : hasSelectedItemRejected
                            ? "Return was rejected for this item. You can't file again."
                            : hasSelectedItemAlreadySubmitted
                              ? "A return request has already been submitted for this item."
                              : "This item cannot be returned."}
                    </span>
                  </Alert>
                )}
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Reason for Return *</Form.Label>
              <Form.Select
                value={formData.return_reason}
                onChange={(e) => setFormData({ ...formData, return_reason: e.target.value })}
                required
              >
                <option value="">Select a reason</option>
                {returnReasons.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Describe the issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={formData.mention_reason}
                onChange={(e) => setFormData({ ...formData, mention_reason: e.target.value })}
                placeholder="Please provide additional details about your return request..."
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Condition Of The Item *</Form.Label>
              <div className="radio-group px-6">
                <Form.Check
                  type="radio"
                  id="condition-unused-tags"
                  name="item_condition"
                  value="unused_with_tags"
                  checked={formData.item_condition === 'unused_with_tags'}
                  onChange={(e) => setFormData({ ...formData, item_condition: e.target.value })}
                  label="Unused, in original condition with tags"
                />
                <Form.Check
                  type="radio"
                  id="condition-unused-no-tags"
                  name="item_condition"
                  value="unused_without_tags"
                  checked={formData.item_condition === 'unused_without_tags'}
                  onChange={(e) => setFormData({ ...formData, item_condition: e.target.value })}
                  label="Unused, without original tags"
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Upload Media *</Form.Label>
              <div
                className={`upload-area ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById('media-upload').click()}
              >
                <input
                  type="file"
                  id="media-upload"
                  accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
                <div className="upload-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
                <p className="upload-text">
                  Drag And Drop Your Video Here (Or) Click To Browse
                </p>
                <p className="upload-hint">
                  Video files (MP4, MOV, AVI, WEBM) up to 50MB
                </p>
              </div>

              {formData.return_video && (
                <div className="video-preview-container">
                  <div className="video-preview-item">
                    <div className="video-placeholder">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="5 3 19 12 5 21 5 3"></polygon>
                      </svg>
                      <span>{formData.return_video.name}</span>
                      <small>{(formData.return_video.size / (1024 * 1024)).toFixed(2)} MB</small>
                    </div>
                    <button
                      type="button"
                      className="remove-media"
                      onClick={removeVideo}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-4 px-6">
              <Form.Check
                type="checkbox"
                id="agree-policy"
                required
                label="I Have Read And Agree To The Return Policy"
              />
            </Form.Group>

            <Button type="submit" className="submit-btn" disabled={!canSubmit}>
              {submitting ? 'Submitting...' : 'SUBMIT RETURN REQUEST'}
            </Button>
          </Form>
        </div>

        {/* Right Side - Return Policy */}
        <div className="return-policy">
          <h2 className="policy-title">Our Return Policy</h2>
          <div className="policy-content">
            <p>Refunds, returns, or exchanges are available only for damaged, defective, or incorrect items.</p>
            <p>Customers must report issues within 24 hours of delivery.</p>
            <p>Returned items must be unused, unwashed, and in original packaging.</p>
            <p>Return courier charges for customer-initiated returns must be borne by the customer.</p>
            <p>Exchanges are only applicable for damaged or incorrect items, not for size or preference changes.</p>
            <p>Approval of refunds or exchanges rests solely with the company after verifying the claim.</p>
          </div>
        </div>
      </div>

     
    </Container>
  );
};

export default ReturnRequest;

